import React from 'react';
import { Link } from 'react-router';
import { isEmpty } from 'lodash';
import classNames from 'classnames';

import ConnectPlatforms from '../connect-platforms/ConnectPlatforms';
import { simpleNotification } from '../../utils/notifications';
import Logo from '../../components/logo/Logo';
import PromptDialog from '../dialog/PromptDialog';
import './ConnectAccounts.scss';

const CONNECT_ACCOUNTS_STORAGE_ORIGIN_KEY = 'connectAccountsStorageOrigin';

class ConnectAccounts extends React.Component {
  constructor(props) {
    super(props);
    this.props = props;

    // Initialize with defaults
    this.state = {
      originalPath: null,
      isLoading: false,
      showSuccessDialog: false,
      showErrorDialog: false,
      errorMessage: '',
      successPlatform: '',
      customSuccessMessage: '',
      specialRedirect: null,
      sourceParam: null,
      redirectAddress: null,
      showDisconnectSuccessDialog: false,
      disconnectPlatform: '',
      pendingOAuth: null,
    };

    // Then apply initialization logic
    const urlParams = this.extractUrlParams();
    const storedParams = this.loadStoredUrlParams();
    const previousPath = this.initializePreviousPath();

    // Update state with extracted values
    this.state = {
      ...this.state,
      ...urlParams,
      ...storedParams,
      originalPath: previousPath,
    };

    // Platform OAuth configuration
    this.platformConfig = {
      twitch: {
        path: '/connect-accounts/twitch',
        requiredParams: ['code'],
        handler: (query, token) => this.props.loginWithTwitchOAuthCode(query.code, query.scope, token, query)
      },
      twitter: {
        path: '/connect-accounts/twitter',
        requiredParams: ['oauth_token', 'oauth_verifier'],
        handler: (query, token) => this.props.loginWithTwitterOAuthCode(query.oauth_token, query.oauth_verifier, token)
      },
      youtube: {
        path: '/connect-accounts/youtube',
        requiredParams: ['code'],
        handler: (query, token) => this.props.loginWithYoutubeOAuthCode(query.code, query.scope, token, query)
      },
      steam: {
        path: '/connect-accounts/steam',
        customHandler: (query, token) => {
          // Guard: ensure token exists
          if (!token) {
            console.error('Cannot process Steam callback: token is undefined');
            return;
          }

          const steamErrors = {
            steam_already_connected: 'This Steam account is already connected to another user account.',
            session_expired: 'Your session has expired. Please try connecting again.',
            user_cancelled: 'Steam connection was cancelled by user.',
            invalid_signature: 'Steam authentication failed. Please try again.',
            invalid_steam_id: 'Invalid Steam ID received. Please try again.',
            user_not_found: 'User not found. Please log in again.',
            steam_profile_error: 'Failed to retrieve Steam profile information.',
            callback_failed: 'Steam connection process failed. Please try again.'
          };

          // Handle backend success redirects
          if (query.success === 'steam_connected') {
            this.props.validateUserHandshake(token, '');
            this.setState({ showSuccessDialog: true, successPlatform: 'Steam' });
          } else if (query.success === 'steam_already_connected') {
            this.props.validateUserHandshake(token,'');
            this.setState({
              showSuccessDialog: true,
              successPlatform: 'Steam',
              customSuccessMessage: 'Your Steam account is already connected to this account.'
            });
          } else if (query.error) {
            this.setState({
              showErrorDialog: true,
              errorMessage: steamErrors[query.error] || 'Steam connection failed. Please try again.'
            });
          } else if (query['openid.ns'] && !query.success && !query.error) {
            // OpenID validation flow
            this.setState({ isLoading: true });
            this.props.loginWithSteamOAuthCode(query, token);
          }
        }
      }
    };

    // Anti-race guard: when landing on `/connect-accounts?oauth=X&status=success`,
    // a stale get-initial-info (fired in parallel from Main.componentDidMount before
    // the callback's DB commit) can overwrite the freshly bound `auth.user.connectedAccounts`
    // with old values. We re-issue validateUserHandshake up to MAX_SUCCESS_HANDSHAKE_RETRIES
    // per platform so the last write on user always reflects the post-bind state.
    this._successHandshakeRetries = {};
  }

  MAX_SUCCESS_HANDSHAKE_RETRIES = 2;

  componentDidMount() {
    this.syncDropsConnectionState();
    this.refreshOnOAuthSuccessUrl(this.props);
  }

  getOAuthSuccessPlatform = (props) => {
    try {
      const query = props?.router?.location?.query || {};
      if (query.status !== 'success' || !query.oauth) return null;
      const platform = String(query.oauth).toLowerCase();
      if (['twitch', 'youtube', 'twitter', 'steam'].includes(platform)) return platform;
    } catch (e) {
      // ignore
    }
    return null;
  }

  refreshOnOAuthSuccessUrl = (props) => {
    const platform = this.getOAuthSuccessPlatform(props);
    if (!platform) return;
    const token = props?.auth?.token;
    if (!token) return;
    if (typeof props.validateUserHandshake !== 'function') return;

    const used = this._successHandshakeRetries[platform] || 0;
    if (used >= this.MAX_SUCCESS_HANDSHAKE_RETRIES) return;

    this._successHandshakeRetries[platform] = used + 1;
    props.validateUserHandshake(token, '');
  }

  getToken = () => {
    return this.props?.auth?.token || null;
  }

  redirectToSignIn = () => {
    const redirectTo = window.location.pathname + (window.location.search || '');
    window.location.href = `/sign-in?redirectto=${encodeURIComponent(redirectTo)}`;
  }

  clearLocalAuth = () => {
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    } catch (e) {
      // ignore
    }
  }

  // When Twitch-side linking is revoked, our UI can remain "connected" until we refresh the user.
  // For Drops flows (src=drops) we validate + refresh on mount of the Connect Accounts landing page.
  syncDropsConnectionState = async () => {
    try {
      const pathname = this.props?.router?.location?.pathname || '';
      const query = this.props?.router?.location?.query || {};
      const source = (query.src || this.state?.sourceParam || '').toString();
      if (source !== 'drops') return;

      // Only run on Connect Accounts pages (but avoid interfering with OAuth callback handling).
      if (!pathname.startsWith('/connect-accounts')) return;
      const isOAuthCallback =
        !!query.code ||
        !!query.oauth_token ||
        !!query.oauth_verifier ||
        !!query['openid.ns'] ||
        !!query.success ||
        !!query.error;
      if (isOAuthCallback) return;

      const token = this.getToken();
      if (!token) return;

      const resp = await fetch('/api/v1/user/drops/link/status?validate=1', {
        method: 'GET',
        headers: { Authorization: `JWT ${token}` },
        credentials: 'same-origin',
      });

      let body = null;
      try { body = await resp.json(); } catch (e) { body = null; }

      if (resp.status === 400 && (body?.code === 'E_FORCED_LOGOUT' || body?.error === 'E_FORCED_LOGOUT')) {
        this.clearLocalAuth();
        this.redirectToSignIn();
        return;
      }

      const statusUser = body?.data?.user || null;
      if (!statusUser) return;

      const reduxTwitchConnected = !!this.props?.auth?.user?.connectedAccounts?.twitch?.id;
      const serverTwitchConnected = !!statusUser.twitchConnected;

      // Only refresh redux user if there's a mismatch (prevents unnecessary calls).
      if (reduxTwitchConnected !== serverTwitchConnected) {
        if (typeof this.props.validateUserHandshake === 'function') {
          this.props.validateUserHandshake(token, '');
        }
      }
    } catch (e) {
      // Never block Connect Accounts due to drops validation issues.
    }
  }

  getOAuthEndpointOverrides = () => {
    try {
      const query = this.props.router?.location?.query || {};
      const overrides = {};

      const twitchClientId = query.twitchClientId ? String(query.twitchClientId).trim() : '';
      const twitchCallbackURL = query.twitchCallbackURL ? String(query.twitchCallbackURL).trim() : '';
      const twitchPermissions = query.twitchPermissions ? String(query.twitchPermissions).trim() : '';

      const steamCallbackURL = query.steamCallbackURL ? String(query.steamCallbackURL).trim() : '';

      const origin = (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : '';

      // IMPORTANT: Keep `redirect_uri` stable and origin-based. Do NOT include dynamic `addr/src` params here,
      // because Twitch may require redirect URIs to match a registered value.
      const computedTwitchCallbackURL = twitchCallbackURL || (origin ? `${origin}/connect-accounts/twitch` : '');
      const computedSteamCallbackURL = steamCallbackURL || (origin ? `${origin}/connect-accounts/steam` : '');

      if (twitchCallbackURL || twitchClientId || twitchPermissions) {
        const params = new URLSearchParams();
        if (computedTwitchCallbackURL) params.set('callbackURL', computedTwitchCallbackURL);
        if (twitchClientId) params.set('clientId', twitchClientId);
        if (twitchPermissions) params.set('permissions', twitchPermissions);
        overrides.twitch = `/api/v1/auth/twitch?${params.toString()}`;
      } else if (computedTwitchCallbackURL) {
        const params = new URLSearchParams();
        params.set('callbackURL', computedTwitchCallbackURL);
        overrides.twitch = `/api/v1/auth/twitch?${params.toString()}`;
      }

      if (steamCallbackURL) {
        const params = new URLSearchParams();
        if (computedSteamCallbackURL) params.set('callbackURL', computedSteamCallbackURL);
        overrides.steam = `/api/v1/auth/steam?${params.toString()}`;
      } else if (computedSteamCallbackURL) {
        const params = new URLSearchParams();
        params.set('callbackURL', computedSteamCallbackURL);
        overrides.steam = `/api/v1/auth/steam?${params.toString()}`;
      }

      return overrides;
    } catch (e) {
      return {};
    }
  }

  isStoredOriginValid = () => {
    try {
      const currentOrigin = window.location.origin;
      const storedOrigin = localStorage.getItem(CONNECT_ACCOUNTS_STORAGE_ORIGIN_KEY);
      if (storedOrigin) return storedOrigin === currentOrigin;

      // Back-compat: older builds didn't store origin. If we can infer from an absolute redirect URL, use that.
      const storedAddr = localStorage.getItem('connectAccountsRedirectAddr');
      if (storedAddr && (storedAddr.startsWith('http://') || storedAddr.startsWith('https://'))) {
        try {
          return new URL(storedAddr).origin === currentOrigin;
        } catch (e) {
          return false;
        }
      }
      return true;
    } catch (e) {
      return false;
    }
  }

  clearStoredCrossOriginParams = () => {
    try {
      localStorage.removeItem(CONNECT_ACCOUNTS_STORAGE_ORIGIN_KEY);
      localStorage.removeItem('connectAccountsSource');
      localStorage.removeItem('connectAccountsRedirectAddr');
      localStorage.removeItem('previousPath');
    } catch (e) {
      // ignore
    }
  }

  extractUrlParams = () => {
    const query = this.props.router?.location?.query;
    if (!query) return {};

    const { src, addr, oauth, status } = query;
    const result = {};

    if (oauth && status === 'error') {
      result.showErrorDialog = true;
      result.errorMessage = `${String(oauth).charAt(0).toUpperCase()}${String(oauth).slice(1)} connection failed. Please try again.`;
    }

    if (src) {
      console.log('Source parameter detected:', src);
      localStorage.setItem('connectAccountsSource', src);
      localStorage.setItem(CONNECT_ACCOUNTS_STORAGE_ORIGIN_KEY, window.location.origin);
      result.sourceParam = src;
    }

    if (addr) {
      const redirectAddress = this.processRedirectAddress(addr, src);
      localStorage.setItem('connectAccountsRedirectAddr', redirectAddress);
      localStorage.setItem(CONNECT_ACCOUNTS_STORAGE_ORIGIN_KEY, window.location.origin);
      result.redirectAddress = redirectAddress;
    }

    return result;
  }

  loadStoredUrlParams = () => {
    const result = {};

    if (!this.isStoredOriginValid()) {
      this.clearStoredCrossOriginParams();
      return result;
    }

    // If origin key is missing (old builds), pin it now to avoid future cross-origin leakage.
    try {
      if (!localStorage.getItem(CONNECT_ACCOUNTS_STORAGE_ORIGIN_KEY)) {
        localStorage.setItem(CONNECT_ACCOUNTS_STORAGE_ORIGIN_KEY, window.location.origin);
      }
    } catch (e) {
      // ignore
    }

    const storedSource = localStorage.getItem('connectAccountsSource');
    const storedAddr = localStorage.getItem('connectAccountsRedirectAddr');

    if (storedSource) {
      result.sourceParam = storedSource;
      console.log('Found existing source parameter:', storedSource);
    }
    if (storedAddr) {
      result.redirectAddress = storedAddr;
      console.log('Found existing redirect address:', storedAddr);
    }

    return result;
  }

  processRedirectAddress = (addr, src) => {
    try {
      let decodedAddr = decodeURIComponent(addr);
      const returnPathMap = { 'gp': '/main' };
      const returnParamMap = { 'gp': 'noiz' };

      try {
        const addrUrl = new URL(decodedAddr);
        const isOnlyOrigin = addrUrl.pathname === '/' || addrUrl.pathname === '';

        if (isOnlyOrigin && src && returnPathMap[src]) {
          decodedAddr = decodedAddr.replace(/\/$/, '') + returnPathMap[src];
          if (returnParamMap[src]) {
            decodedAddr += `?src=${returnParamMap[src]}`;
          }
        } else if (src && returnParamMap[src]) {
          const addrUrlWithParam = new URL(decodedAddr);
          addrUrlWithParam.searchParams.set('src', returnParamMap[src]);
          decodedAddr = addrUrlWithParam.toString();
        }
      } catch (urlError) {
        console.error('Error processing addr URL:', urlError);
      }
      console.log('Final redirect address:', decodedAddr);
      return decodedAddr;
    } catch (e) {
      console.error('Error decoding redirect address:', e);
      return null;
    }
  }

  initializePreviousPath = () => {
    const isOAuthCallback = this.props.router?.location?.pathname.match(/\/(steam|twitter|youtube|twitch)$/);
    if (this.state.redirectAddress || isOAuthCallback) return null;

    const referrer = document.referrer;
    if (!referrer) {
      return localStorage.getItem('previousPath');
    }

    try {
      const referrerUrl = new URL(referrer);
      const currentUrl = new URL(window.location.href);
      const isDifferentDomain = referrerUrl.hostname !== currentUrl.hostname;
      const oauthDomains = ['steamcommunity.com', 'twitter.com', 'accounts.google.com', 'youtube.com', 'twitch.tv', 'id.twitch.tv'];
      const isOAuthReferrer = oauthDomains.some(domain => referrerUrl.hostname.includes(domain));

      if (isDifferentDomain && !isOAuthReferrer) {
        let redirectUrl = referrer;
        if (this.state.sourceParam) {
          const returnParamMap = { 'gp': 'noiz' };
          const returnParam = returnParamMap[this.state.sourceParam];
          if (returnParam) {
            const redirectUrlObj = new URL(referrer);
            redirectUrlObj.searchParams.set('src', returnParam);
            redirectUrl = redirectUrlObj.toString();
          }
        }
        localStorage.setItem('connectAccountsRedirectAddr', redirectUrl);
        return null;
      } else {
        const path = referrerUrl.pathname + referrerUrl.search;
        if (path && !path.includes('connect-accounts')) {
          localStorage.setItem('previousPath', path);
          return path;
        }
      }
    } catch (e) {
      console.error('Error parsing referrer:', e);
    }

    return localStorage.getItem('previousPath');
  }

  isConnectAccountsOAuthPlatform = (platform) => {
    return platform === 'twitch' || platform === 'youtube';
  }

  getOAuthReturnPath = (platform, status) => {
    let returnTo = '/connect-accounts';
    try {
      returnTo = sessionStorage.getItem('oauthReturnTo') || returnTo;
      sessionStorage.removeItem('oauthReturnTo');
    } catch (e) {
      // ignore
    }

    const separator = returnTo.indexOf('?') === -1 ? '?' : '&';
    return `${returnTo}${separator}oauth=${encodeURIComponent(platform)}&status=${encodeURIComponent(status)}`;
  }

  replaceToOAuthResult = (platform, status) => {
    this.props.router.replace(this.getOAuthReturnPath(platform, status));
  }

  processOAuthCallback = (pathname, query, token) => {
    // Process all OAuth platforms using configuration
    Object.entries(this.platformConfig).forEach(([platform, config]) => {
      if (pathname !== config.path) return;

      if (this.isConnectAccountsOAuthPlatform(platform) && query.error) {
        this.replaceToOAuthResult(platform, 'error');
        return;
      }

      const hasStandardParams = !!(config.requiredParams && config.requiredParams.every(param => !isEmpty(query[param])));
      const hasSteamParams = !!(platform === 'steam' && (query.success || query.error || query['openid.ns']));
      const hasAnyParams = hasStandardParams || hasSteamParams;

      if (!token) {
        if (hasAnyParams) {
          this.setState({ isLoading: true, pendingOAuth: { platform, query } });
        }
        return;
      }

      // Standard OAuth flow
      if (hasStandardParams) {
        this.setState({ isLoading: true, pendingOAuth: null });
        config.handler(query, token);
      }
      // Custom handlers (e.g., Steam with multiple callback types)
      if (config.customHandler && hasSteamParams) {
        this.setState({ pendingOAuth: null });
        config.customHandler(query, token);
      }
    });
  }

  UNSAFE_componentWillMount() {
    // const { token } = this.props.auth;
    const pathname = this.props.router.location.pathname;
    const query = this.props.router.location.query;
    const currentToken = this.props.auth?.token;
    this.processOAuthCallback(pathname, query, currentToken);
    // if (
    //   this.props.router.location.pathname === '/connect-accounts/tiktok'
    // ) {
    //   const { code, scopes, state } = this.props.router.location.query;
    //   console.log('tiktok auth code received');
    //   console.log('code', this.props);
    //   this.props.loginWithTikTokOAuthCode(code, scopes, state, token);
    //   console.log('steam auth code received');
    // }
    // if (
    //   this.props.router.location.pathname === '/connect-accounts/facebook'
    // ) {
    //   const { code, scopes, state } = this.props.router.location.query;
    //   console.log('tiktok auth code received');
    //   console.log('code', this.props);
    //   this.props.loginWithFacebookOAuthCode(code, scopes, state, token);
    //   console.log('steam auth code received');
    // }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    // If we landed on a callback URL before redux auth hydrated, retry once token is available.
    if (this.state.pendingOAuth && nextProps?.auth?.token) {
      const pathname = nextProps.router.location.pathname;
      const query = nextProps.router.location.query;
      this.processOAuthCallback(pathname, query, nextProps.auth.token);
    }

    // On the OAuth success URL, re-issue handshake when either token just hydrated
    // or the just-bound connectedAccounts entry got wiped by a stale get-initial-info response.
    const successPlatform = this.getOAuthSuccessPlatform(nextProps);
    if (successPlatform) {
      const tokenJustAvailable = !this.props?.auth?.token && !!nextProps?.auth?.token;
      const prevId = this.props?.auth?.user?.connectedAccounts?.[successPlatform]?.id;
      const nextId = nextProps?.auth?.user?.connectedAccounts?.[successPlatform]?.id;
      const idWasOverwritten = !!prevId && !nextId;
      if (tokenJustAvailable || idWasOverwritten) {
        this.refreshOnOAuthSuccessUrl(nextProps);
      }
    }

    ['youtube', 'twitter', 'twitch', 'steam'].forEach(platform => {
      this.handleConnectionStateChange(platform, nextProps);
      this.handleDisconnectionStateChange(platform, nextProps);
    });
  }

  handleConnectionStateChange = (platform, nextProps) => {
    const currentAuth = this.props.auth[platform];
    const nextAuth = nextProps.auth[platform];

    if (!currentAuth || !nextAuth) return;

    // Only show connection modal if we're not in a disconnect operation
    // Check both that isDisconnecting is false in both states
    if (
      currentAuth.isLoading === true &&
      nextAuth.isLoading === false &&
      !currentAuth.isDisconnecting &&
      !nextAuth.isDisconnecting
    ) {
      if (this.isConnectAccountsOAuthPlatform(platform)) {
        this.setState({ isLoading: false });
        this.replaceToOAuthResult(platform, nextAuth.errors.hasError ? 'error' : 'success');
        return;
      }

      // Guard: ensure token exists before using it
      const token = this.props.auth?.token;
      if (!token) {
        console.warn('Cannot validate handshake: token is undefined');
        return;
      }

      this.props.validateUserHandshake(token, '');
      this.setState({ isLoading: false });

      if (nextAuth.errors.hasError) {
        this.setState({
          showErrorDialog: true,
          errorMessage: nextAuth.errors.message
        });
      } else {
        this.setState({ showSuccessDialog: true, successPlatform: this.formatPlatformName(platform) });

        if (localStorage.getItem('backToBooth') === 'true') {
          localStorage.removeItem('backToBooth');
          this.setState({ specialRedirect: '/make-some-noiz?connected=true' });
        }
      }
    }
  }

  handleDisconnectionStateChange = (platform, nextProps) => {
    const currentAuth = this.props.auth[platform];
    const nextAuth = nextProps.auth[platform];

    if (!currentAuth || !nextAuth) return;

    // Handle the case where isDisconnecting might be undefined initially
    const wasDisconnecting = currentAuth.isDisconnecting === true;
    const isNowDisconnected = nextAuth.isDisconnecting === false;

    if (wasDisconnecting && isNowDisconnected) {
      if (nextAuth.errors.hasError) {
        this.setState({
          showErrorDialog: true,
          errorMessage: nextAuth.errors.message
        });
      } else {
        this.setState({
          showDisconnectSuccessDialog: true,
          disconnectPlatform: this.formatPlatformName(platform)
        });
      }
    }
  }

  // 处理成功对话框关闭
  handleSuccessDialogClose = () => {
    this.setState({
      showSuccessDialog: false,
      customSuccessMessage: '' // 重置自定义消息
    });

    // 如果有特殊重定向，使用它；否则停留在当前页
    if (this.state.specialRedirect) {
      this.props.router.push(this.state.specialRedirect);
    }
  }

  goBack = () => {
    const originOk = this.isStoredOriginValid();
    const sourceParam = (originOk ? localStorage.getItem('connectAccountsSource') : null) || this.state.sourceParam;
    const redirectAddr = (originOk ? localStorage.getItem('connectAccountsRedirectAddr') : null) || this.state.redirectAddress;

    if (this.attemptExternalRedirect(redirectAddr, sourceParam)) return;
    if (this.attemptSourceMapping(sourceParam)) return;
    if (this.attemptPreviousPath()) return;

    // Default redirect to quests
    console.log('✅ No saved path found, redirecting to /quests');
    this.props.router.push('/quests');
  }

  attemptExternalRedirect = (redirectAddr, sourceParam) => {
    if (!redirectAddr || redirectAddr === 'null' || redirectAddr === 'undefined') return false;

    if (redirectAddr.startsWith('http://') || redirectAddr.startsWith('https://')) {
      console.log('✅ Redirecting to specified address:', redirectAddr);
      this.clearStoredParams(['connectAccountsSource', 'connectAccountsRedirectAddr', 'previousPath']);
      window.location.href = redirectAddr;
      return true;
    }

    console.warn('⚠️ Invalid redirect address format (missing http/https):', redirectAddr);
    localStorage.removeItem('connectAccountsRedirectAddr');
    return false;
  }

  attemptSourceMapping = (sourceParam) => {
    if (!sourceParam || sourceParam === 'null' || sourceParam === 'undefined') return false;

    const sourceUrlMap = { 'gp': 'https://goldenpoop.io/exchange?src=noiz' };
    const targetUrl = sourceUrlMap[sourceParam];

    if (targetUrl && (targetUrl.startsWith('http://') || targetUrl.startsWith('https://'))) {
      console.log('✅ Redirecting to mapped URL for source:', targetUrl);
      this.clearStoredParams(['connectAccountsSource', 'previousPath']);
      window.location.href = targetUrl;
      return true;
    }

    console.log('⚠️ No valid URL mapping found for source:', sourceParam);
    localStorage.removeItem('connectAccountsSource');
    return false;
  }

  attemptPreviousPath = () => {
    const previousPath = localStorage.getItem('previousPath') || this.state.originalPath;

    if (previousPath && previousPath !== 'null' && previousPath !== 'undefined') {
      console.log('✅ Using saved path for navigation:', previousPath);
      localStorage.removeItem('previousPath');
      this.props.router.push(previousPath);
      return true;
    }

    return false;
  }

  clearStoredParams = (keys) => {
    keys.forEach(key => localStorage.removeItem(key));
  }

  formatPlatformName = (platform) => {
    return platform.charAt(0).toUpperCase() + platform.slice(1);
  }

  normalizeConnectedAccount = (account) => {
    // Standardize all connected account objects to { username: null, id: null }
    // when disconnected, instead of leaving all original properties as null
    if (!account || (account.username === null && account.id === null)) {
      return { username: null, id: null };
    }
    return account;
  }

  displayNotConnectedMessage = () => {
    simpleNotification({
      level: 'warning',
      title: '',
      message: 'You must connect at least one streaming platform in order to see available quests.',
    });
  }

  // 添加错误对话框处理方法
  handleErrorDialogClose = () => {
    console.log('Error dialog closed');
    this.setState({ showErrorDialog: false });

    // 关闭错误对话框后，保留在当前页面
    // 不执行goBack，让用户可以重试
  }
  closeConnectAccounts = () => {
    this.setState({
      showSuccessDialog: false,
      showErrorDialog: false,
      showDisconnectSuccessDialog: false,
      customSuccessMessage: '',
      disconnectPlatform: ''
    }, this.goBack);
  }

  // 添加断开连接成功对话框处理方法
  handleDisconnectSuccessDialogClose = () => {
    console.log('Disconnect success dialog closed');
    this.setState({
      showDisconnectSuccessDialog: false,
      disconnectPlatform: ''
    });

    // Clear query parameters from URL after disconnect
    const cleanUrl = window.location.pathname;
    window.history.replaceState({}, document.title, cleanUrl);
  }

  render() {
    const { isLoading, showSuccessDialog, showErrorDialog, errorMessage, successPlatform, customSuccessMessage, showDisconnectSuccessDialog, disconnectPlatform } = this.state;

    // 如果正在加载，显示加载动画
    if (isLoading) {
      return (
        <section className="ConnectAccounts loading-container">
          <div className="loading-spinner">
            <i className="fa fa-spinner fa-spin fa-3x fa-fw" />
            <span>Connecting account, please wait...</span>
          </div>
        </section>
      );
    }

    return (
      <section className="ConnectAccounts">
        {showSuccessDialog && (
          <PromptDialog
            dialogOpen={true}
            level="success"
            title="Connection Successful!"
            content={customSuccessMessage || `Your ${successPlatform || 'Twitch'} account has been successfully connected`}
            cancelAction={this.handleSuccessDialogClose}
            cancelButtonName="CLOSE"
          />
        )}
        {/* 断开连接成功对话框 */}
        {showDisconnectSuccessDialog && (
          <PromptDialog
            dialogOpen={true}
            level="success"
            title="Disconnection Successful!"
            content={`Your ${disconnectPlatform || 'account'} has been successfully disconnected`}
            cancelAction={this.handleDisconnectSuccessDialogClose}
            cancelButtonName="CLOSE"
          />
        )}
        {/* 错误对话框 */}
        {showErrorDialog && (
          <PromptDialog
            dialogOpen={true}
            level="failure"
            title="Connection Failed"
            content={errorMessage || 'Could not connect your account. Please try again.'}
            cancelAction={this.handleErrorDialogClose}
            cancelButtonName="CLOSE"
          />
        )}
        <section className="ModalMaster">
          <section className={classNames('Modal Modal__welcome active')}>
            <div
              className="Modal__layer"
              onClick={this.closeConnectAccounts}
              role="button"
              aria-hidden
            />
            <section className="Modal__wrapper">
              <div className="Modal__icon">
                <Logo />
                <button
                  className="Modal__close-btn"
                  onClick={this.closeConnectAccounts}
                  type="button"
                  aria-label="Close connected accounts"
                />
              </div>
              <div className="Welcome__header">
                Connect Your Accounts
              </div>
              <ConnectPlatforms
                auth={this.props.auth}
                config={this.props.config}
                oauthEndpointOverrides={this.getOAuthEndpointOverrides()}
                disconnectTwitch={this.props.disconnectTwitch}
                disconnectYoutube={this.props.disconnectYoutube}
                disconnectTwitter={this.props.disconnectTwitter}
                disconnectSteam={this.props.disconnectSteam}
              />
              <div className="Welcome__footer">
                <button onClick={this.goBack}>Let's Start</button>
                <Link className="logout" to="/logout" href="/logout">Logout</Link>
              </div>
              <div className="blurb">
                Designed by game developers and gamers, for game developers and gamers
              </div>
            </section>
          </section>
        </section>
      </section>
    );
  }
}

export default ConnectAccounts;
